function hypot3(ax, ay, az, bx, by, bz) {
  return Math.hypot(ax - bx, ay - by, az - bz);
}

function rotSumDiff(a, b) {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y) + Math.abs(a.z - b.z);
}

function readVec3(v) {
  return { x: v?.x ?? 0, y: v?.y ?? 0, z: v?.z ?? 0 };
}

function readEuler(r) {
  if (!r) return { x: 0, y: 0, z: 0 };
  return {
    x: r._x ?? r.x ?? 0,
    y: r._y ?? r.y ?? 0,
    z: r._z ?? r.z ?? 0,
  };
}

function readMouse(m) {
  return { x: m?.x ?? 0, y: m?.y ?? 0 };
}

function snapshotPayload(payload) {
  const field = payload?.field;
  if (field === "cursor" && payload.update?.cursor) {
    const c = payload.update.cursor;
    const p = readVec3(c.position);
    const r = readEuler(c.rotation);
    const m = readMouse(c.mouse);
    return {
      field,
      artboard: c.artboard,
      page: c.page,
      px: p.x,
      py: p.y,
      pz: p.z,
      rx: r.x,
      ry: r.y,
      rz: r.z,
      mx: m.x,
      my: m.y,
    };
  }
  if (field === "preview" && payload.update?.preview) {
    const pr = payload.update.preview;
    const p = readVec3(pr.position);
    const r = readEuler(pr.rotation);
    const m = readMouse(pr.mouse);
    return {
      field,
      artboard: pr.artboard,
      page: pr.page,
      px: p.x,
      py: p.y,
      pz: p.z,
      rx: r.x,
      ry: r.y,
      rz: r.z,
      mx: m.x,
      my: m.y,
    };
  }
  return null;
}

function changedEnough(prevSnap, nextSnap, eps) {
  if (!nextSnap) return true;
  if (!prevSnap) return true;
  if (prevSnap.field !== nextSnap.field) return true;
  if (String(prevSnap.artboard) !== String(nextSnap.artboard)) return true;
  if (prevSnap.page !== nextSnap.page) return true;

  const posD = hypot3(prevSnap.px, prevSnap.py, prevSnap.pz, nextSnap.px, nextSnap.py, nextSnap.pz);
  if (posD >= eps.pos) return true;

  const rotD = rotSumDiff(
    { x: prevSnap.rx, y: prevSnap.ry, z: prevSnap.rz },
    { x: nextSnap.rx, y: nextSnap.ry, z: nextSnap.rz }
  );
  if (rotD >= eps.rot) return true;

  const mouseD = Math.hypot(prevSnap.mx - nextSnap.mx, prevSnap.my - nextSnap.my);
  if (mouseD >= eps.mouse) return true;

  return false;
}

/**
 * Coalesces to one network send per animation frame with the latest payload.
 * Small epsilons avoid spam; `forceResendMs` bypasses epsilon so slow drifts still ship on time.
 */
export function createThrottledPresenceSender(sendFn, options = {}) {
  const {
    posEpsilon = 0.004,
    rotEpsilon = 0.005,
    mouseEpsilon = 0.0006,
    forceResendMs = 64,
    maxSilenceMs = 6000,
  } = options;

  const eps = { pos: posEpsilon, rot: rotEpsilon, mouse: mouseEpsilon };
  let lastSnap = null;
  let lastPayload = null;
  /** @type {number | null} */
  let lastSendAt = null;
  let rafId = null;

  const trySend = () => {
    const p = lastPayload;
    if (!p) return;
    const nextSnap = snapshotPayload(p);
    const now = Date.now();
    const timeSince = lastSendAt == null ? Infinity : now - lastSendAt;
    const bypassEpsilon = timeSince >= forceResendMs;
    if (!bypassEpsilon && !changedEnough(lastSnap, nextSnap, eps)) return;
    sendFn(p);
    lastSnap = nextSnap;
    lastSendAt = now;
  };

  const schedule = () => {
    if (rafId != null) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      trySend();
    });
  };

  let intervalId = null;
  if (maxSilenceMs > 0) {
    const tick = Math.min(2000, Math.max(500, Math.floor(maxSilenceMs / 3)));
    intervalId = window.setInterval(() => {
      if (!lastPayload || lastSendAt == null) return;
      if (Date.now() - lastSendAt < maxSilenceMs) return;
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      lastSnap = null;
      trySend();
    }, tick);
  }

  return {
    push(payload) {
      if (payload != null) lastPayload = payload;
      schedule();
    },
    /** Bypass epsilon — use after artboard/context change. */
    force(payload) {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      lastSnap = null;
      if (payload != null) lastPayload = payload;
      const p = payload ?? lastPayload;
      if (p) {
        sendFn(p);
        lastSnap = snapshotPayload(p);
        lastSendAt = Date.now();
      }
    },
    cancel() {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    },
    flush() {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      trySend();
    },
  };
}
